<!--
  - Copyright (c) ProviderTrust LLC.
  - All Rights Reserved.
  -
  - This software is confidential and proprietary information of
  - ProviderTrust LLC ("Confidential Information"). You shall not disclose
  - such Confidential Information and shall use it only in
  - accordance with the terms of the license agreement you entered
  - into with ProviderTrust.
-->

<template>
  <v-navigation-drawer
    class="slide-in-drawer"
    v-model="drawerVisibility"
    absolute
    temporary
    right
    width="960"
    height="auto"
  >
    <div class="wrapper">
      <div class="two-columns">
        <div class="left-column">
          <h1 v-if="Object.keys(slideIn.details || {}).length > 1">
            Records to Review
          </h1>
          <section
            class="section-detail"
            v-for="detail in slideIn.details"
            :key="detail.title"
          >
            <h2>{{ detail.title }}</h2>
            <table class="property-table">
              <tr
                v-for="(value, name) in detail.data"
                :key="name"
                v-if="value && !name.startsWith('_')"
              >
                <th>{{ name | patchWord }}</th>
                <td v-if="name === 'View Details'">
                  <a :href="value" @click.prevent="toExternal"
                    >Click here to view full record</a
                  >
                </td>
                <td
                  v-else-if="name === 'Status'"
                  :style="{ color: statusColor(value) }"
                >
                  {{ value }}
                </td>
                <td v-else-if="name === 'Source'">
                  <a
                    v-if="detail.data['_link']"
                    :href="detail.data['_link']"
                    target="_blank"
                    >{{ value }}
                  </a>
                  <div v-else class="table-cell-wrapper">{{ value }}</div>
                </td>
                <td v-else>{{ value }}</td>
              </tr>
            </table>
          </section>
        </div>
        <div class="right-column" v-if="slideIn.userData">
          <section class="section-user-detail">
            <h2>{{ slideIn.userData.title }}</h2>
            <table class="property-table">
              <tr
                v-for="(value, name) in slideIn.userData.data"
                :key="name"
                v-if="value"
              >
                <th>{{ name | patchWord }}</th>
                <td>{{ value }}</td>
              </tr>
            </table>
          </section>
        </div>
      </div>

      <div class="section-change-history">
        <h2>
          {{ !hasClearNonBtn ? "Review Status & History" : "Review Status" }}
        </h2>
        <section>
          <table class="property-table">
            <tr>
              <th>Review Status</th>
              <td>
                <status-dropdown-dialog
                  v-if="!hasClearNonBtn"
                  :disabled="!drawerVisibility"
                  :status="status"
                  :status-infos="statusInfos"
                  @status-change="emitEvent($event)"
                />
                <div v-else class="clear-status">Clear</div>
              </td>
            </tr>
          </table>
        </section>

        <v-data-table
          class="ec-table"
          v-if="!hasClearNonBtn"
          :headers="changeHistoryHeaders"
          :items="changeHistorys"
          sort-icon="arrow_drop_up"
          :disable-initial-sort="true"
          hide-actions
        >
          <template slot="items" slot-scope="props">
            <td>{{ getStatusLabel(props.item.newStatus) }}</td>
            <td>{{ props.item.modifiedBy }}</td>
            <td>{{ props.item.modifiedDate }}</td>
            <td>{{ props.item.notes }}</td>
          </template>
        </v-data-table>
      </div>

      <a @click.stop="drawerVisibility = false"
        >Return to all results
        <v-icon>fa-angle-double-right</v-icon>
      </a>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from "vuex"
import StatusDropdownDialog from "@/components/StatusDropdownDialog"
import EventBus from "@/common/event-bus"
import { statusColor } from "@/common/constants"

const patchDictionary = {
  "Date Acquired From Source": "Date Acquired"
}

export default {
  name: "Notification",
  data: () => ({
    drawerVisibility: false,
    changeHistoryHeaders: [
      {
        text: "Status Changed To",
        value: "newStatus"
      },
      {
        text: "Updated By",
        value: "modifiedBy"
      },
      {
        text: "Date",
        value: "modifiedDate"
      },
      {
        text: "note",
        value: "notes"
      }
    ],
    statusColor
  }),
  created() {
    EventBus.$on("delete-state", prop => {
      delete history.state[prop]
      history.replaceState(history.state, null, null)
    })
  },
  methods: {
    toExternal(evt) {
      EventBus.$emit("to-external", states =>
        history.replaceState(
          {
            ...history.state,
            ...states
          },
          null,
          null
        )
      )

      window.location.href = evt.srcElement.href
    },
    getStatusLabel(status) {
      return this.statusInfos[status].text
    },
    emitEvent(evt) {
      EventBus.$emit("global-status-change" + this.eventModifier, evt)
    }
  },
  filters: {
    patchWord: word => patchDictionary[word] || word
  },
  computed: {
    ...mapState({
      slideIn: state => state.notification.slideIn,
      statusInfos: state => state.notification.statusInfos,
      hasClearNonBtn: state => state.notification.hasClearNonBtn,
      eventModifier: state => state.notification.eventModifier
    }),
    status() {
      return this.slideIn.matchStatus
    },
    changeHistorys() {
      return this.slideIn.changeHistorys || []
    }
  },
  watch: {
    slideIn(newVal) {
      if (newVal && !this.drawerVisibility) {
        this.drawerVisibility = true
        this.$el.scrollTop = 0
      }
    }
  },
  components: { StatusDropdownDialog }
}
</script>

<style scoped lang="scss">
.slide-in-drawer {
  position: fixed; // default is absolute

  .wrapper {
    padding: 1.875rem;
    height: 100%;
  }

  h2 {
    @include label-font(1rem);
    margin-bottom: 0.5rem;
    padding-top: 1.5rem;
  }

  h1 {
    @include label-font(1.5rem);
  }

  > a {
    .v-icon {
      color: currentColor;
      margin-left: 0.375rem;
    }
  }

  .two-columns {
    display: flex;
    justify-content: space-between;
    .left-column,
    .right-column {
      width: 100%;
    }
  }
  .property-table {
    border-collapse: collapse;
    width: 100%;
    border: 1px solid $color-ec-light4-gray;
    th {
      width: 11.625rem;
      background-color: $color-ec-blue;
      color: $color-white;
      font-weight: $normal;
      border-bottom: 1px solid $color-off-white;
      text-align: left;
    }
    td {
      font-size: 0.875rem;
    }
    /**/
    tr {
      height: 2rem;
      &:nth-child(even) td {
        background-color: $color-off-white;
      }
    }

    th,
    td {
      padding: 0 0.75rem;
    }
  }
  section {
    width: 90%;
  }
  .section-user-detail {
    .property-table th {
      background-color: $color-ec-table-gray;
    }
  }
  .section-change-history {
    margin: 1.5rem 0;

    ::v-deep .status-dropdown {
      width: 100%; // TODO not exactly sure why it is necessary here but not in any other places to make dropdown menu width work
    }
    .ec-table ::v-deep {
      margin-top: 0.75rem;
      tr:nth-child(even) {
        background-color: $color-off-white;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Notification.vue